import { Search, UserPlus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { LetterAvatar } from '@/components/ui/letter-avatar';
import { Spinner } from '@/components/ui/spinner';
import { useUsers } from '@/modules/common/application/use-users';
import { ProjectRole, User, UserAccountStatus } from '@/modules/common/types';

import { useProjects } from '../hooks/use-projects';
import { ProjectRoleSelect } from './ProjectRoleSelect';

interface AddProjectMemberDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SelectedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export const AddProjectMemberDialog = ({
  projectId,
  open,
  onOpenChange,
}: AddProjectMemberDialogProps) => {
  const { t } = useTranslation();
  const { getProjectMembers, inviteMembers } = useProjects();
  const { internalUsers } = useUsers();
  const projectMembers = getProjectMembers(projectId);

  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
  const [selectedRole, setSelectedRole] = useState<ProjectRole>(ProjectRole.MEMBER);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedTagIndex, setSelectedTagIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get existing project member IDs for filtering
  const existingMemberIds = useMemo(() => {
    return new Set(projectMembers.map((member) => member.userId));
  }, [projectMembers]);

  // Filter users based on search query and exclude already selected users
  const filteredUsers = useMemo(() => {
    const activeUsers = internalUsers.filter(
      (user) => user.status === UserAccountStatus.USER_INFO_COMPLETED
    );

    const selectedUserIds = new Set(selectedUsers.map((user) => user.id));

    let filtered = activeUsers.filter((user) => !selectedUserIds.has(user.id));

    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter((user) => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        return (
          fullName.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  }, [internalUsers, selectedUsers, searchQuery]);

  const handleUserSelect = useCallback(
    (user: User) => {
      if (existingMemberIds.has(user.id)) return; // Prevent selecting existing members

      const selectedUser: SelectedUser = {
        id: user.id,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email,
      };

      setSelectedUsers((prev) => [...prev, selectedUser]);
      setSearchQuery(''); // Clear search but keep dropdown open
      // Don't close dropdown - allow multiple selections
    },
    [existingMemberIds]
  );

  const handleUserRemove = useCallback((userId: string) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== userId));
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Handle tag deletion when input is empty
      if (
        (e.key === 'Backspace' || e.key === 'Delete') &&
        !searchQuery &&
        selectedUsers.length > 0
      ) {
        e.preventDefault();
        if (selectedTagIndex >= 0) {
          // Delete selected tag
          const userToRemove = selectedUsers[selectedTagIndex];
          handleUserRemove(userToRemove.id);
          setSelectedTagIndex(-1);
        } else {
          // Select last tag for deletion
          setSelectedTagIndex(selectedUsers.length - 1);
        }
        return;
      }

      // Handle tag navigation when input is empty
      if (
        !searchQuery &&
        selectedUsers.length > 0 &&
        (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
      ) {
        e.preventDefault();
        if (e.key === 'ArrowLeft') {
          setSelectedTagIndex((prev) =>
            prev < 0 ? selectedUsers.length - 1 : Math.max(0, prev - 1)
          );
        } else {
          setSelectedTagIndex((prev) => (prev >= selectedUsers.length - 1 ? -1 : prev + 1));
        }
        return;
      }

      // Clear tag selection when typing
      if (e.key.length === 1) {
        setSelectedTagIndex(-1);
      }

      // Handle dropdown navigation
      if (!isDropdownOpen || filteredUsers.length === 0) {
        if (e.key === 'Escape') {
          setIsDropdownOpen(false);
          setSelectedTagIndex(-1);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < filteredUsers.length - 1 ? prev + 1 : 0));
          setSelectedTagIndex(-1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredUsers.length - 1));
          setSelectedTagIndex(-1);
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredUsers.length) {
            const selectedUser = filteredUsers[highlightedIndex];
            if (!existingMemberIds.has(selectedUser.id)) {
              handleUserSelect(selectedUser);
            }
          }
          break;
        case 'Escape':
          setIsDropdownOpen(false);
          setHighlightedIndex(-1);
          setSelectedTagIndex(-1);
          break;
        case 'Home':
          e.preventDefault();
          setHighlightedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setHighlightedIndex(filteredUsers.length - 1);
          break;
      }
    },
    [
      isDropdownOpen,
      filteredUsers,
      highlightedIndex,
      existingMemberIds,
      handleUserSelect,
      searchQuery,
      selectedUsers,
      selectedTagIndex,
      handleUserRemove,
    ]
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Only open dropdown if there's content to search
    setIsDropdownOpen(value.trim().length > 0);
    setHighlightedIndex(0); // Reset to first item when filtering
  }, []);

  const handleInputFocus = useCallback(() => {
    // Only open dropdown if there's a search query
    if (searchQuery.trim()) {
      setIsDropdownOpen(true);
      setHighlightedIndex(0); // Highlight first item when opening
    }
  }, [searchQuery]);

  const handleInputBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    // Only close if not clicking on dropdown
    if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
      setTimeout(() => setIsDropdownOpen(false), 150);
    }
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        dropdownRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleInvite = async () => {
    if (selectedUsers.length === 0) return;

    setIsLoading(true);
    try {
      const userIds = selectedUsers.map((user) => user.id);
      await inviteMembers({ projectId, userIds, role: selectedRole });

      setSelectedUsers([]);
      setSelectedRole(ProjectRole.MEMBER);
      setSearchQuery('');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to invite members:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      // Reset form when closing
      setSelectedUsers([]);
      setSelectedRole(ProjectRole.MEMBER);
      setSearchQuery('');
      setIsDropdownOpen(false);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('modules.mavue2.project.members.inviteTitle')}</DialogTitle>
          <DialogDescription>
            {t('modules.mavue2.project.members.inviteDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2 w-full">
            <Label htmlFor="invite-role">{t('modules.mavue2.project.members.role')}</Label>
            <ProjectRoleSelect
              className="w-full"
              value={selectedRole}
              onValueChange={setSelectedRole}
            />
          </div>
          <div className="flex-1 space-y-2">
            <Label>{t('modules.mavue2.project.members.teamMembers')}</Label>
            <div className="relative">
              {/* Input Container with Tags */}
              <div className="flex flex-wrap items-start gap-2 min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />

                {/* Selected User Tags */}
                {selectedUsers.map((user, index) => (
                  <div
                    key={user.id}
                    className={`flex items-center gap-1.5 bg-secondary text-secondary-foreground rounded-md px-2 py-1 text-xs ${
                      selectedTagIndex === index ? 'ring-2 ring-ring' : ''
                    }`}
                  >
                    <LetterAvatar
                      name={`${user.firstName} ${user.lastName}`.trim() || user.email}
                      size="xs"
                    />
                    <span className="font-medium">
                      {`${user.firstName} ${user.lastName}`.trim() || user.email}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleUserRemove(user.id)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {/* Input Field */}
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={
                    selectedUsers.length === 0
                      ? t('modules.mavue2.project.members.searchPlaceholder')
                      : ''
                  }
                  value={searchQuery}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className="flex-1 min-w-[120px] bg-transparent outline-none placeholder:text-muted-foreground"
                />
              </div>

              {/* Custom Dropdown */}
              {isDropdownOpen && searchQuery.trim() && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-[200px] overflow-y-auto"
                >
                  {filteredUsers.length === 0 && searchQuery && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      {t('modules.mavue2.project.members.noTeamMembersFound')}
                    </div>
                  )}
                  {filteredUsers.length === 0 && !searchQuery && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      {t('modules.mavue2.project.members.startTyping')}
                    </div>
                  )}
                  {filteredUsers.map((user, index) => {
                    const isExistingMember = existingMemberIds.has(user.id);
                    const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                    const isHighlighted = index === highlightedIndex;
                    return (
                      <div
                        key={user.id}
                        onClick={() => !isExistingMember && handleUserSelect(user)}
                        className={`flex items-center gap-3 px-3 py-2 ${
                          isHighlighted
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent hover:text-accent-foreground'
                        } ${isExistingMember ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        ref={
                          isHighlighted
                            ? (el) => el?.scrollIntoView({ block: 'nearest' })
                            : undefined
                        }
                      >
                        <LetterAvatar name={displayName || user.email} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {displayName || user.email}
                          </div>
                        </div>
                        {isExistingMember && (
                          <span className="text-xs text-muted-foreground">
                            {t('modules.mavue2.project.members.alreadyMember')}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleDialogClose(false)}>
            {t('modules.mavue2.common.cancel')}
          </Button>
          <Button onClick={handleInvite} disabled={selectedUsers.length === 0 || isLoading}>
            {isLoading && <Spinner className="mr-2" />}
            <UserPlus className="mr-2 h-4 w-4" />
            {t('modules.mavue2.project.members.sendInvitation')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
